import React, { useCallback, useEffect, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { transformProjectType } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-project-type/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import {
	ContextualAnalyticsData,
	MountEvent,
	MODAL,
	fireScreenAnalyticsDeferred,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export const ExistingGICWarning = () => {
	const [{ wasModalRetriggered }, { resetWasModalRetriggered }] = useTriggerIssueCreateModal();
	const [{ resetFormAction, selectedProject, isFormDirty: isCreateFormDirty }] = useFormContext();
	const { formatMessage } = useIntl();

	const projectId = selectedProject && selectedProject.value;
	const projectType = selectedProject ? transformProjectType(selectedProject.projectType) : '';

	const { publish } = useGICLifeCycleEvents();

	// Re-initialise context values when create button is clicked and the form is clean
	useEffect(() => {
		if (wasModalRetriggered && !isCreateFormDirty) {
			resetWasModalRetriggered(true);
		}
	}, [wasModalRetriggered, isCreateFormDirty, resetWasModalRetriggered]);

	const onClickDiscardButton = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'discardExistingGicWarning', {
				projectType,
			});
			resetWasModalRetriggered(true);

			resetFormAction && resetFormAction();
			publish(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_EXISTING_IN_PROGRESS_ISSUE, {});
		},
		[publish, projectType, resetWasModalRetriggered, resetFormAction],
	);

	const onClickKeepEditingButton = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'keepEditingExistingGicWarning', {
				projectType,
			});
			resetWasModalRetriggered();
			publish(GIC_LIFECYCLE_EVENTS.MODAL_KEEP_EDITING_EXISTING_ISSUE, {});
		},
		[projectType, publish, resetWasModalRetriggered],
	);

	if (!wasModalRetriggered || !isCreateFormDirty || !selectedProject) return null;

	return (
		<ContextualAnalyticsData
			sourceType={MODAL}
			sourceName="existingGicWarning"
			containerType="project"
			containerId={projectId || ''}
			attributes={{ projectType }}
		>
			<MountEvent onMount={fireScreenAnalyticsDeferred} />
			<ShortcutScope>
				<Modal
					onClose={() => resetWasModalRetriggered()}
					width="medium"
					testId="issue-create.ui.modal.existing-gic-warning.modal-dialog"
					messageId="issue-create.ui.modal.existing-gic-warning.modal"
					messageType="transactional"
				>
					<ModalHeader>
						<ModalTitle appearance="warning">
							{formatMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.headingIssueTermRefresh
									: messages.heading,
							)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.descriptionIssueTermRefresh
								: messages.description,
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={onClickDiscardButton}
							appearance="subtle"
							testId="issue-create.ui.modal.existing-gic-warning.discard-button"
						>
							{formatMessage(messages.discardButton)}
						</Button>
						<Button
							onClick={onClickKeepEditingButton}
							appearance="warning"
							testId="issue-create.ui.modal.existing-gic-warning.keep-editing"
						>
							{formatMessage(messages.keepEditingButton)}
						</Button>
					</ModalFooter>
				</Modal>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};
